import { Box, Typography, TypographyProps } from '@mui/material';
import React, { useEffect, useState } from 'react';

type TransitionTextProps = {
    transitionTexts: string[];
    constantTextBefore: string;
    constantTextAfter: string;
    interval: number;
    color: string;
    transitionTextColor: string;
    variant: TypographyProps['variant'];
};

// Animation styles as constants
const animationStyles = `
  @keyframes enter {
    0% {
      opacity: 0;
      transform: translateY(100%); /* Start from below */
    }
    100% {
      opacity: 1;
      transform: translateY(0); /* Fully visible */
    }
  }

  @keyframes exit {
    0% {
      opacity: 1;
      transform: translateY(0); /* Fully visible */
    }
    100% {
      opacity: 0;
      transform: translateY(-100%); /* Move up and out */
    }
  }
`;

// Add <style> tag dynamically to include the animations
const addAnimationStyles = (): void => {
    if (!document.getElementById('transition-text-animations')) {
        const styleTag = document.createElement('style');
        styleTag.id = 'transition-text-animations';
        styleTag.innerHTML = animationStyles;
        document.head.appendChild(styleTag);
    }
};

const TransitionText: React.FC<TransitionTextProps> = ({
    transitionTexts,
    constantTextBefore,
    constantTextAfter,
    interval = 2000,
    color,
    transitionTextColor,
    variant,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        addAnimationStyles();
        const timer = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % transitionTexts.length);
        }, interval);
        return () => clearInterval(timer);
    }, [transitionTexts.length, interval]);

    const getAnimationStyle = (isEntering: boolean, isExiting: boolean): string => {
        if (isEntering) return 'enter 1s ease-in-out';
        if (isExiting) return 'exit 1s ease-in-out';
        return 'none';
    };

    return (
        <Typography alignItems={'center'} color={color} component={'div'} variant={variant}>
            {constantTextBefore + ' '}
            <Box
                display="inline-block"
                position="relative"
                sx={(theme) => ({
                    height: '2.5rem',
                    overflow: 'hidden',
                    textAlign: 'start',
                    verticalAlign: 'top',
                    width: '6em',
                    [theme.breakpoints.up('xxl')]: {
                        height: '5rem', // Increase height on XXL screens
                    },
                })}
            >
                {transitionTexts.map((text, index) => {
                    const isEntering = index === currentIndex;
                    const isExiting = index === (currentIndex - 1 + transitionTexts.length) % transitionTexts.length;
                    return (
                        <Box
                            key={`${text}-${index}`}
                            sx={{
                                animation: getAnimationStyle(isEntering, isExiting),
                                animationFillMode: 'forwards',
                                left: 0,
                                opacity: isEntering || isExiting ? 1 : 0,
                                position: 'absolute',
                                width: '100%',
                            }}
                        >
                            <Typography color={transitionTextColor} variant={variant}>
                                {' ' + text}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <br />
            {constantTextAfter}
        </Typography>
    );
};

export default TransitionText;
